export type ApiErrorCode =
  | 'TICKET_EXPIRED'
  | 'BAD_REQUEST'
  | 'CAPTCHA_REJECTED'
  | 'PAYLOAD_TOO_LARGE'
  | 'RATE_LIMITED'
  | 'SAP_UNAVAILABLE'
  | 'SERVER_ERROR'
  | 'NETWORK'
  | 'UNKNOWN';

export interface ApiError extends Error {
  name: 'ApiError';
  status: number;
  code: ApiErrorCode;
  /** Mensaje del backend, apto para mostrar al usuario cuando el status es 400. */
  serverMessage: string | null;
  /** Segundos indicados por el header Retry-After en un 429. */
  retryAfter: number | null;
  /** Identificador para cruzar con los logs del backend. No se muestra al usuario. */
  correlationId: string | null;
}

/**
 * Detecta el 400 de ticket expirado/consumido frente al 400 de validación.
 *
 * El backend no expone todavía un código estable para diferenciarlos, así que
 * se recurre al texto del mensaje. Aislado aquí para poder sustituirlo por el
 * código en un solo sitio cuando lo publiquen.
 */
const isTicketExpired = (body: Record<string, unknown>): boolean => {
  const code = String(body.code ?? '');
  if (code) return code === 'TICKET_EXPIRED';
  const text = String(body.msg ?? body.message ?? '');
  return /expir/i.test(text);
};

const mapStatusToCode = (
  status: number,
  body: Record<string, unknown>,
): ApiErrorCode => {
  if (status === 0) return 'NETWORK';
  if (status === 400) return isTicketExpired(body) ? 'TICKET_EXPIRED' : 'BAD_REQUEST';
  if (status === 403) return 'CAPTCHA_REJECTED';
  if (status === 413) return 'PAYLOAD_TOO_LARGE';
  if (status === 429) return 'RATE_LIMITED';
  if (status === 502 || status === 503 || status === 504) return 'SAP_UNAVAILABLE';
  if (status >= 500) return 'SERVER_ERROR';
  return 'UNKNOWN';
};

/** Traduce un ApiErrorCode a su clave de i18n bajo `$vuetify.errores`. */
export const errorI18nKey = (error: ApiError): string => {
  if (error.code === 'RATE_LIMITED') {
    return error.retryAfter
      ? '$vuetify.errores.demasiadosIntentos'
      : '$vuetify.errores.demasiadosIntentosEspera';
  }
  const keys: Record<ApiErrorCode, string> = {
    TICKET_EXPIRED: '$vuetify.errores.sesionExpirada',
    BAD_REQUEST: '$vuetify.errores.datosInvalidos',
    CAPTCHA_REJECTED: '$vuetify.errores.captchaRechazado',
    PAYLOAD_TOO_LARGE: '$vuetify.errores.archivoDemasiadoGrande',
    RATE_LIMITED: '$vuetify.errores.demasiadosIntentosEspera',
    SAP_UNAVAILABLE: '$vuetify.errores.servicioNoDisponible',
    SERVER_ERROR: '$vuetify.errores.generico',
    NETWORK: '$vuetify.errores.sinConexion',
    UNKNOWN: '$vuetify.errores.generico',
  };
  return keys[error.code];
};

export const isApiError = (error: unknown): error is ApiError => (
  error instanceof Error && error.name === 'ApiError'
);

/**
 * Normaliza un error de axios a un ApiError con semántica de dominio.
 *
 * @param error Error capturado por el interceptor de respuesta.
 */
export const toApiError = (error: any): ApiError => {
  const status: number = error?.response?.status ?? 0;
  const body: Record<string, unknown> = error?.response?.data ?? {};
  const headers: Record<string, string> = error?.response?.headers ?? {};
  const serverMessage = (body.msg ?? body.message ?? null) as string | null;

  return Object.assign(new Error(serverMessage ?? error?.message ?? 'Error'), {
    name: 'ApiError' as const,
    status,
    code: mapStatusToCode(status, body),
    serverMessage,
    retryAfter: Number(headers['retry-after']) || null,
    correlationId: (body.correlationId as string)
      ?? headers['x-request-id']
      ?? null,
  });
};
